import React from "react";
import PropTypes from "prop-types";
import { kebabCase } from "lodash";
import { Helmet } from "react-helmet";
import { graphql, Link, withPrefix } from "gatsby";
import { ImStopwatch } from "react-icons/im";
import { BiCalendarAlt } from "react-icons/bi";
import Layout from "../components/Layout";
import Content, { HTMLContent } from "../components/Content";
import MermaidContent from "../components/MermaidContent";
import ShareButtons from "../components/share/share.component";
import Quiz from "../components/Quiz/Quiz";
import { Disqus } from "gatsby-plugin-disqus";

const SHOW_DISQUS = false;
const SHOW_AD_WIDGET = false;

const stripHtml = (text) =>
  String(text || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildMailchimpJsonpUrl = (actionUrl, email) => {
  if (!actionUrl || !email) {
    return null;
  }

  try {
    const parsed = new URL(actionUrl);
    const u = parsed.searchParams.get("u");
    const id = parsed.searchParams.get("id");
    const fId = parsed.searchParams.get("f_id");

    if (!u || !id) {
      return null;
    }

    const jsonpPath = parsed.pathname.replace(/\/post\/?$/, "/post-json");
    const jsonpUrl = new URL(`${parsed.origin}${jsonpPath}`);
    jsonpUrl.searchParams.set("u", u);
    jsonpUrl.searchParams.set("id", id);
    if (fId) {
      jsonpUrl.searchParams.set("f_id", fId);
    }
    jsonpUrl.searchParams.set("EMAIL", email);

    return jsonpUrl;
  } catch (error) {
    return null;
  }
};

export const BlogPostTemplate = ({
  content,
  contentComponent,
  description,
  tags,
  relatedPosts,
  featuredimage,
  title,
  readingTime,
  date,
  id,
  questions,
  mailchimpActionUrl,
}) => {
  const PostContent = contentComponent || Content;
  const useMermaidRenderer = PostContent === HTMLContent;
  const titles = `Leer --> ${title} `;
  const tagss = tags;
  const url = typeof window !== "undefined" ? window.location.href : "";
  const twitterHandle = "crisjc8";
  const disqusConfig = {
    url: url,
    identifier: id,
    title: title,
  };
  const preguntas = questions;
  const image = featuredimage?.publicURL
    ? `https://www.crisweb.me${featuredimage.publicURL}`
    : `https://www.crisweb.me${withPrefix("/") + "img/skeleton-blog.png"}`;

  const normalizedMailchimpActionUrl =
    typeof mailchimpActionUrl === "string"
      ? mailchimpActionUrl.replace(/&amp;/g, "&").trim()
      : "";

  const canSubmitNewsletter =
    normalizedMailchimpActionUrl.includes("/subscribe/post?") &&
    normalizedMailchimpActionUrl.includes("u=") &&
    normalizedMailchimpActionUrl.includes("id=");

  const [newsletterEmail, setNewsletterEmail] = React.useState("");
  const [newsletterState, setNewsletterState] = React.useState("idle");
  const [newsletterFeedback, setNewsletterFeedback] = React.useState("");

  const handleNewsletterSubmit = (event) => {
    event.preventDefault();

    if (!canSubmitNewsletter) {
      setNewsletterState("error");
      setNewsletterFeedback("Configura primero la URL de Mailchimp.");
      return;
    }

    if (!newsletterEmail || !newsletterEmail.includes("@")) {
      setNewsletterState("error");
      setNewsletterFeedback("Ingresa un email valido.");
      return;
    }

    const jsonpUrl = buildMailchimpJsonpUrl(
      normalizedMailchimpActionUrl,
      newsletterEmail,
    );

    if (!jsonpUrl) {
      setNewsletterState("error");
      setNewsletterFeedback(
        "No se pudo preparar el formulario de suscripcion.",
      );
      return;
    }

    setNewsletterState("loading");
    setNewsletterFeedback("Enviando...");

    const callbackName = `mailchimpCb_${Date.now()}_${Math.floor(
      Math.random() * 10000,
    )}`;
    const script = document.createElement("script");
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      try {
        delete window[callbackName];
      } catch (error) {
        window[callbackName] = undefined;
      }
    };

    window[callbackName] = (response) => {
      cleanup();

      if (response && response.result === "success") {
        setNewsletterState("success");
        setNewsletterFeedback(
          "Suscripcion completada. Revisa tu correo para confirmar.",
        );
        setNewsletterEmail("");
        return;
      }

      setNewsletterState("error");
      setNewsletterFeedback(
        stripHtml(response && response.msg) ||
          "No se pudo suscribir ahora. Intenta mas tarde.",
      );
    };

    timeoutId = window.setTimeout(() => {
      cleanup();
      setNewsletterState("error");
      setNewsletterFeedback(
        "Tiempo agotado. Intenta nuevamente en unos minutos.",
      );
    }, 12000);

    script.onerror = () => {
      cleanup();
      setNewsletterState("error");
      setNewsletterFeedback("Error de red. Intenta mas tarde.");
    };

    jsonpUrl.searchParams.set("c", callbackName);
    script.src = jsonpUrl.toString();
    document.body.appendChild(script);
  };

  const renderNewsletterForm = (extraClass = "") => (
    <form
      action={canSubmitNewsletter ? normalizedMailchimpActionUrl : undefined}
      method="post"
      className={`newsletter-form ${extraClass}`.trim()}
      onSubmit={handleNewsletterSubmit}
    >
      <input
        type="email"
        name="EMAIL"
        placeholder="tu@email.com"
        value={newsletterEmail}
        onChange={(event) => setNewsletterEmail(event.target.value)}
        disabled={newsletterState === "loading"}
        required
      />
      <button
        type="submit"
        disabled={!canSubmitNewsletter || newsletterState === "loading"}
        className={
          newsletterState === "success"
            ? "is-success"
            : newsletterState === "error"
            ? "is-error"
            : ""
        }
      >
        {canSubmitNewsletter
          ? newsletterState === "loading"
            ? "Enviando..."
            : newsletterState === "success"
            ? "✓ Suscrito"
            : newsletterState === "error"
            ? "✕ Reintentar"
            : "Suscribirme"
          : "Configurar Mailchimp"}
      </button>
      {newsletterFeedback && (
        <p
          className={`newsletter-feedback ${newsletterState}`}
          aria-live="polite"
        >
          {newsletterFeedback}
        </p>
      )}
    </form>
  );

  return (
    <section className="section pl-4">
      <Helmet>
        <title>{`${title} | Blog`}</title>
        <meta name="description" content={description} />

        {/* Open Graph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={image} />
        <meta property="og:image:secure_url" content={image} />
        <meta property="og:site_name" content="Blog Crisweb" />
        <meta property="og:locale" content="es_ES" />

        {/* Twitter */}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
        <link rel="canonical" href={url} />
      </Helmet>
      <div className="container content">
        <div className="columns blog-post-layout">
          <div className="column is-12-mobile is-8-tablet is-8-desktop is-offset-1-desktop">
            <h1 className="title is-size-4-mobile is-size-3-tablet is-size-3 has-text-weight-bold is-bold-light">
              {title}
            </h1>
            <p>{description}</p>
            <p>
              {" "}
              <BiCalendarAlt /> {date} ,<ImStopwatch /> {readingTime}
            </p>
            <span style={{ color: "#345B83" }}>Share with:</span>
            <div>
              <ShareButtons
                title={titles}
                url={url}
                twitterHandle={twitterHandle}
                tags={tagss}
              />
            </div>
            {useMermaidRenderer ? (
              <MermaidContent content={content} className="blog-post-content" />
            ) : (
              <PostContent content={content} className="blog-post-content" />
            )}
            {preguntas && preguntas.length > 0 && (
              <Quiz questions={preguntas} />
            )}
            {tags && tags.length ? (
              <div
                style={{
                  marginTop: "4rem",
                  borderTop: "2px solid #6A70B2",
                  borderBottom: "2px solid #6A70B2",
                  paddingTop: "2rem",
                  paddingBottom: "2rem",
                }}
              >
                <h2
                  style={{
                    fontSize: "1.8rem",
                    marginBottom: "1.5rem",
                    color: "#333",
                  }}
                >
                  Explora más temas
                </h2>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    display: "flex",
                    flexWrap: "wrap",
                  }}
                >
                  {tags.map((tag) => (
                    <li key={tag + "tag"} style={{ margin: "0.5rem" }}>
                      <Link
                        to={`/tags/${kebabCase(tag)}/`}
                        style={{
                          textDecoration: "none",
                          border: "1px solid #6A70B2",
                          color: "#6A70B2",
                          padding: "0.5rem 1rem",
                          borderRadius: "0.5rem",
                          transition: "background-color 0.3s, color 0.3s",
                          display: "inline-block",
                          cursor: "pointer",
                          fontWeight: "normal",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#6A70B2";
                          e.target.style.color = "#fff";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.color = "#6A70B2";
                        }}
                      >
                        {tag}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div
                style={{
                  marginTop: "2rem",
                  padding: "1rem 0",
                  color: "#6b7280",
                }}
              >
                <p>Este articulo aun no tiene tags asociados.</p>
              </div>
            )}

            <section className="blog-mobile-newsletter is-hidden-desktop">
              <h3>Arquitectura en 5 minutos</h3>
              <p>Recibe nuevos posts y patrones directamente en tu correo.</p>
              {renderNewsletterForm("mobile-newsletter-form")}
            </section>

            {SHOW_DISQUS ? <Disqus config={disqusConfig} /> : null}
          </div>

          <aside className="column is-12-mobile is-3-desktop is-hidden-touch">
            <div className="blog-sidebar-sticky">
              <section className="blog-sidebar-card">
                <h3>Newsletter</h3>
                <p>
                  Recibe estrategias de arquitectura, patrones y nuevos posts en
                  tu correo.
                </p>
                {renderNewsletterForm()}
              </section>

              <section className="blog-sidebar-card">
                <h3>Temas Relacionados</h3>
                {relatedPosts && relatedPosts.length > 0 ? (
                  <ul className="related-posts-list">
                    {relatedPosts.map((post) => (
                      <li key={post.id}>
                        <Link to={post.fields.slug}>
                          {post.frontmatter.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No hay temas relacionados por ahora.</p>
                )}
              </section>

              <section className="blog-sidebar-card">
                <h3>Tags</h3>
                {tags && tags.length > 0 ? (
                  <div className="sidebar-tags-cloud">
                    {tags.map((tag) => (
                      <Link
                        key={`sidebar-${tag}`}
                        to={`/tags/${kebabCase(tag)}/`}
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p>No hay tags para este articulo.</p>
                )}
              </section>

              {SHOW_AD_WIDGET ? (
                <section className="blog-sidebar-card ad-widget">
                  <h3>Publicidad</h3>
                  <p>Este es el bloque exclusivo para Google Ads en el post.</p>
                  <div
                    id="google-ads-sidebar-slot"
                    className="ad-placeholder"
                    data-ad-provider="google-adsense"
                    data-ad-slot="sidebar-single"
                  >
                    300 x 250
                  </div>
                </section>
              ) : null}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

BlogPostTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  contentComponent: PropTypes.func,
  description: PropTypes.string,
  title: PropTypes.string,
  readingTime: PropTypes.string,
  date: PropTypes.string,
  id: PropTypes.string,
  featuredimage: PropTypes.object,
  relatedPosts: PropTypes.array,
  mailchimpActionUrl: PropTypes.string,
};

const BlogPost = ({ data }) => {
  const { markdownRemark: post } = data;
  const tags = post.frontmatter.tags || [];

  const allPosts = data.allMarkdownRemark.edges.map((edge) => edge.node);
  const relatedByTags = allPosts.filter(
    (candidate) =>
      candidate.id !== post.id &&
      candidate.frontmatter.tags &&
      candidate.frontmatter.tags.some((tag) => tags.includes(tag)),
  );

  const relatedPosts =
    relatedByTags.length > 0
      ? relatedByTags.slice(0, 4)
      : allPosts.filter((candidate) => candidate.id !== post.id).slice(0, 4);

  const mailchimpActionUrl = process.env.GATSBY_MAILCHIMP_ACTION || "";

  return (
    <Layout>
      <BlogPostTemplate
        content={post.html}
        readingTime={post.fields.readingTime.text}
        contentComponent={HTMLContent}
        description={post.frontmatter.description}
        helmet={
          <Helmet titleTemplate="%s | Blog">
            <title>{`${post.frontmatter.title}`}</title>
            <meta
              name="description"
              content={`${post.frontmatter.description}`}
            />
          </Helmet>
        }
        date={post.frontmatter.date}
        tags={tags}
        relatedPosts={relatedPosts}
        mailchimpActionUrl={mailchimpActionUrl}
        title={post.frontmatter.title}
        featuredimage={post.frontmatter.featuredimage}
        questions={post.frontmatter.questions}
        id={post.frontmatter.id}
      />
      <HTMLContent content={post.frontmatter.references} />
      <div dangerouslySetInnerHTML={{ __html: post.frontmatter.references }} />
    </Layout>
  );
};

BlogPost.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
  }),
};

export default BlogPost;

export const pageQuery = graphql`
  query BlogPostByID($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      html
      frontmatter {
        date(formatString: "MMMM DD, YYYY")
        title
        description
        tags
        featuredimage {
          publicURL
        }
        questions {
          question
          options
          correctAnswer
          feedback
        }
      }
      fields {
        readingTime {
          text
        }
      }
    }
    allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      filter: { frontmatter: { templateKey: { eq: "blog-post" } } }
      limit: 30
    ) {
      edges {
        node {
          id
          fields {
            slug
          }
          frontmatter {
            title
            tags
          }
        }
      }
    }
  }
`;
